import { hungerMoment } from "@/moment";
import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import classNames from "classnames";
import { Moment } from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ConditionalFragment from "../General/ConditionalFragment";
import Flexbox from "../General/Flexbox";
import Modal from "../General/Modal/Modal";

function getAWeek(startFrom: Moment) {
  // a week: [Sun, Mon, Tue, Wed, Thur, Friday, Sat]
  const aWeek: Moment[] = Array(7);
  // 2022/11/1 is Tue, return 2
  const startFromDay = startFrom.day();
  // The first day of a week is Sunday. 10/30 is Sunday
  const sunday = startFrom.clone().subtract(startFromDay, "day");

  for (let i = 0; i < 7; i++) {
    aWeek[i] = sunday.clone();
    sunday.add(1, "day");
  }
  return {
    aWeek: aWeek,
    nextWeekStartFrom: sunday,
  };
}

interface DateItem {
  date: Moment;
  isToday: boolean;
  isPickedDate: boolean;
  isCurrentMonth: boolean;
}

function getMonth(
  currentMonth: Moment,
  now: Moment,
  pickedDate: Moment
): DateItem[][] {
  // now: 2022/11/10
  const dates = [];
  // the first of the month 2022/11/1
  const startFromDate = currentMonth.clone().startOf("month");
  const startMonth = startFromDate.month();

  for (let start = startFromDate; start.month() === startMonth; ) {
    const { aWeek, nextWeekStartFrom } = getAWeek(start);
    start = nextWeekStartFrom;
    dates.push(
      aWeek.map((date) => {
        return {
          date: date,
          isToday: date.isSame(now, "day"),
          isPickedDate: date.isSame(pickedDate, "day"),
          isCurrentMonth: date.isSame(currentMonth, "month"),
        };
      })
    );
  }
  return dates;
}

const dayText = ["日", "一", "二", "三", "四", "五", "六"];

interface Props {
  pickedDate: Moment;
  onPick: (pickedDate: Moment) => void;
}

const DatePicker: React.FC<Props> = ({ pickedDate, onPick }) => {
  const [openCalendarModal, setOpenCalendarModal] = useState<boolean>(false);

  const trigger = useCallback(() => setOpenCalendarModal((prev) => !prev), []);

  return (
    <Fragment>
      <CalendarModal
        pickedDate={pickedDate}
        onPick={(picked) => {
          setOpenCalendarModal(false);
          onPick(picked);
        }}
        onClose={trigger}
        openCalendarModal={openCalendarModal}
      />
      <button
        type={"button"}
        onClick={trigger}
        className={classNames(
          "flex-1",
          "flex justify-between items-center",
          "px-3 py-1.5",
          "text-grey-2",
          "border border-grey-6 rounded-lg"
        )}
      >
        {pickedDate.format("YYYY / MM / DD")}
        <div className={classNames("ml-12", "text-grey-3")}>
          <FontAwesomeIcon icon={faCalendarAlt} />
        </div>
      </button>
    </Fragment>
  );
};

export default DatePicker;

export type PeriodSearchHandler = (config: {
  startAt: Moment;
  endAt: Moment;
}) => void;

export const PeriodDataPicker: React.FC<{
  onSearch?: PeriodSearchHandler;
  onStartDateChange?: (startAt: Moment) => void;
  onEndDateChange?: (endAt: Moment) => void;
  defaultPeriod?: {
    startAt?: Moment;
    endAt?: Moment;
  };
  className?: string;
}> = ({
  onSearch,
  onStartDateChange,
  onEndDateChange,
  className,
  defaultPeriod,
}) => {
  const [startAt, setStartAt] = useState<Moment>(
    defaultPeriod?.startAt ?? hungerMoment().startOf("month")
  );
  const [endAt, setEndAt] = useState<Moment>(
    defaultPeriod?.endAt ?? hungerMoment()
  );

  useEffect(() => {
    onStartDateChange?.(startAt);
  }, [startAt, onStartDateChange]);

  useEffect(() => {
    onEndDateChange?.(endAt);
  }, [endAt, onEndDateChange]);

  const onSearchClick = useCallback(() => {
    onSearch?.({
      startAt,
      endAt,
    });
  }, [startAt, endAt, onSearch]);

  return (
    <Flexbox
      direction={"column"}
      justify={"start"}
      className={classNames(className, "md:flex-row", "md:items-center")}
    >
      <Flexbox align={"center"} className={classNames("mb-5", "md:mb-0")}>
        <span
          className={classNames("mr-2.5", "text-base text-grey-3", "md:hidden")}
        >
          開始
        </span>
        <DatePicker pickedDate={startAt} onPick={setStartAt} />
      </Flexbox>
      <span
        className={classNames(
          "mx-2.5",
          "text-grey-3 text-xl",
          "hidden",
          "md:block"
        )}
      >
        {"-"}
      </span>
      <Flexbox align={"center"} className={classNames("mb-5", "md:mb-0")}>
        <span
          className={classNames("mr-2.5", "text-base text-grey-3", "md:hidden")}
        >
          結束
        </span>
        <DatePicker pickedDate={endAt} onPick={setEndAt} />
      </Flexbox>
      {onSearch && (
        <button
          type="button"
          onClick={onSearchClick}
          className={
            "btn__primary md:ml-auto w-full md:w-[6.25rem] h-9 rounded-extreme"
          }
        >
          搜尋
        </button>
      )}
    </Flexbox>
  );
};

interface CalendarProps {
  openCalendarModal: boolean;
  pickedDate: Moment;
  onPick: (picked: Moment) => void;
  onClose?: () => void;
}

const CalendarModal: React.FC<CalendarProps> = ({
  openCalendarModal,
  pickedDate,
  onPick,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Moment>(pickedDate);
  // use to highlight today
  const now = hungerMoment();

  const dates = useMemo(() => {
    return getMonth(currentMonth, now, pickedDate);
  }, [currentMonth, now, pickedDate]);

  const title = useMemo(() => {
    return `${currentMonth.year()} 年 ${currentMonth.month() + 1} 月`;
  }, [currentMonth]);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((currentMonth) => currentMonth.clone().add(1, "month"));
  }, []);

  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth((currentMonth) =>
      currentMonth.clone().subtract(1, "month")
    );
  }, []);

  const onContainerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation(),
    []
  );

  return (
    <ConditionalFragment condition={openCalendarModal}>
      <Modal onClick={onClose}>
        <Flexbox
          direction={"column"}
          className={classNames("bg-white", "rounded-md", "overflow-hidden")}
          onClick={onContainerClick}
        >
          {/* select month */}
          <Flexbox
            justify={"between"}
            align={"center"}
            className={classNames(
              "w-full",
              "px-4 py-2",
              "border-b border-solid border-grey-4"
            )}
          >
            <button
              type="button"
              className={classNames("w-10 h-10", "text-grey-3")}
              onClick={handlePreviousMonth}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className={classNames("text-grey-1", "font-semibold")}>
              {title}
            </div>
            <button
              type={"button"}
              className={classNames("w-10 h-10", "text-grey-3")}
              onClick={handleNextMonth}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </Flexbox>
          {/* date container*/}
          <Flexbox
            direction={"column"}
            className={classNames("w-full", "px-4 py-2")}
          >
            {/* a week */}
            <ul
              className={classNames(
                "flex justify-between",
                "text-grey-2 text-sm"
              )}
            >
              {dayText.map((text) => {
                return (
                  <li
                    key={text}
                    className={classNames(
                      "w-full",
                      "p-3",
                      "text-center",
                      "font-medium"
                    )}
                  >
                    {text}
                  </li>
                );
              })}
            </ul>

            <Flexbox direction={"column"} className={classNames("text-grey-2")}>
              {dates.map((week, index) => {
                return (
                  <Flexbox
                    justify={"between"}
                    key={index}
                    className={classNames("w-full")}
                  >
                    {week.map((dateItem) => {
                      const { date, isToday, isPickedDate, isCurrentMonth } =
                        dateItem;

                      return (
                        <button
                          key={date.valueOf()}
                          className={classNames(
                            "w-full",
                            "p-1.5",
                            "m-1",
                            "text-sm text-center",
                            "font-medium",
                            "rounded-full",
                            {
                              "text-primary-pink": isToday,
                              "text-grey-3": !isCurrentMonth,
                              "text-white font-bold bg-primary-pink":
                                isPickedDate,
                            }
                          )}
                          onClick={() => onPick(date)}
                        >
                          {date.date()}
                        </button>
                      );
                    })}
                  </Flexbox>
                );
              })}
            </Flexbox>
          </Flexbox>
        </Flexbox>
      </Modal>
    </ConditionalFragment>
  );
};
